import React, { useState, useEffect, useCallback, useContext } from "react";
import { Container, Row, Col, Card, Button, Form, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BsStarFill, BsCartPlus, BsBagCheck, BsArrowRight, BsSearch, BsTagsFill, BsGridFill } from "react-icons/bs";
import { SERVER_PATH } from "../../config/env";
import { UserContext } from "../../context/userContext";
import { getLocalStorage } from "../../utils/helper";
import { userInfo } from "../../utils/appInfo";
import { successToaster, errorToaster } from "../../utils";
import { ToastContainer } from "react-toastify";
import "./uniqueProducts.css";

import { formatProductText } from "../../utils/productTextFormatter";

export default function UniqueProducts() {
  const navigate = useNavigate();
  const { userData, updateUserData } = useContext(UserContext);
  const isLogin = getLocalStorage("uid") ? true : false;

  const getUserId = () => {
    const user = userInfo();
    return user?.email || getLocalStorage("uid") || (userData && userData.email) || "";
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter & Sort State
  const [sortOption, setSortOption] = useState("featured");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUniqueProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER_PATH}get/unique_products.php`, {
        params: {
          status: 1, // Only active products
          featured: featuredFilter,
          category: categoryFilter,
          search: searchQuery,
          sort: sortOption
        }
      });

      if (res.data && res.data.status === "success") {
        setProducts(res.data.products || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.log("Error fetching unique products:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [sortOption, categoryFilter, featuredFilter, searchQuery]);

  useEffect(() => {
    document.title = "Sivam Farms Unique Products - Premium Herbal & Organic Catalog";
    window.scrollTo(0, 0);
    fetchUniqueProducts();
  }, [fetchUniqueProducts]);

  // Extract unique categories for filter
  const categoriesList = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));

  // Add to Cart Handler
  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    if (!isLogin) {
      navigate(`/profile/${product.product_id}`);
      return;
    }

    const userId = getUserId();
    if (!userId) {
      errorToaster({ msg: "Please log in to add products to cart" });
      navigate(`/profile/${product.product_id}`);
      return;
    }

    axios
      .post(`${SERVER_PATH}post/cart.php`, {
        product_id: product.product_id,
        user_id: userId,
        quantity: 1
      })
      .then(() => {
        successToaster({ msg: `"${product.product_name}" added to cart!` });
        // Update Context Cart
        if (userData?.cart) {
          updateUserData({ cart: [...userData.cart, product] });
        }
      })
      .catch((err) => {
        console.log(err);
        errorToaster({ msg: "Failed to add product to cart." });
      });
  };

  // Buy Now Handler
  const handleBuyNow = (e, product) => {
    e.stopPropagation();
    if (!isLogin) {
      navigate(`/profile/${product.product_id}`);
      return;
    }

    const userId = getUserId();
    if (!userId) {
      errorToaster({ msg: "Please log in to buy products" });
      navigate(`/profile/${product.product_id}`);
      return;
    }

    axios
      .post(`${SERVER_PATH}post/cart.php`, {
        product_id: product.product_id,
        user_id: userId,
        quantity: 1
      })
      .then(() => {
        navigate(`/cart/${product.product_id}`);
      })
      .catch((err) => {
        console.log(err);
        errorToaster({ msg: "Failed to add product to cart." });
      });
  };

  // View Details Handler
  const handleViewDetails = (productId) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="unique-products-container pb-5">
      {/* HERO BANNER */}
      <div className="unique-hero-banner mb-5">
        <Container>
          <span className="badge bg-light text-success px-3 py-2 rounded-pill fw-bold text-uppercase mb-3 shadow-sm">
            Handpicked Premium Catalog
          </span>
          <h1 className="unique-hero-title mb-3">SIVAM FARMS UNIQUE PRODUCTS</h1>
          <p className="unique-hero-subtitle">
            Discover our carefully selected premium herbal products made with traditional ingredients, crafted for pure wellness, and trusted by thousands of happy customers.
          </p>
        </Container>
      </div>

      <Container>
        {/* FILTER & SORT CONTROLS BAR */}
        <div className="unique-filter-bar mb-5">
          <Row className="g-3 align-items-center">
            {/* Search Input */}
            <Col lg={4} md={5}>
              <div className="input-group">
                <span className="input-group-text bg-light border-0">
                  <BsSearch className="text-success" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by product name, category, or highlight..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-0 bg-light shadow-none"
                />
              </div>
            </Col>

            {/* Category Filter */}
            <Col lg={3} sm={6}>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-pill bg-light border-0 shadow-none fw-semibold text-secondary"
              >
                <option value="all">All Categories</option>
                {categoriesList.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Featured Filter */}
            <Col lg={2} sm={6}>
              <Form.Select
                value={featuredFilter}
                onChange={(e) => setFeaturedFilter(e.target.value)}
                className="rounded-pill bg-light border-0 shadow-none fw-semibold text-secondary"
              >
                <option value="all">All Products</option>
                <option value="1">Featured Only ⭐</option>
              </Form.Select>
            </Col>

            {/* Sort Options */}
            <Col lg={3} md={12} className="ms-auto">
              <Form.Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="rounded-pill bg-light border-0 shadow-none fw-bold text-success"
              >
                <option value="featured">Sort: Featured First</option>
                <option value="newest">Sort: Newest First</option>
                <option value="price_low_high">Price: Low to High</option>
                <option value="price_high_low">Price: High to Low</option>
                <option value="best_selling">Best Selling</option>
                <option value="alphabetical">Alphabetical (A-Z)</option>
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* PRODUCT GRID */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading unique products...</span>
            </div>
            <p className="text-muted mt-3 fw-semibold">Loading Sivam Farms Unique Products...</p>
          </div>
        ) : products.length > 0 ? (
          <Row className="g-4">
            {products.map((item) => {
              const regularPrice = parseInt(item.price) || 0;
              const offerPrice = parseInt(item.offer_price) || 0;
              const activePrice = offerPrice > 0 ? offerPrice : regularPrice;
              const hasDiscount = offerPrice > 0 && regularPrice > offerPrice;
              const discountPercentage = hasDiscount ? Math.round(((regularPrice - offerPrice) / regularPrice) * 100) : 0;

              return (
                /* Desktop: 4 Columns (col-lg-3), Laptop: 3 Columns (col-md-4), Tablet: 2 Columns (col-sm-6), Mobile: 1 Column (col-12) */
                <Col key={item.unique_id} col={12} sm={6} md={4} lg={3}>
                  <Card className="unique-product-card">
                    {/* Card Image & Badges */}
                    <div className="unique-card-img-wrapper" onClick={() => handleViewDetails(item.product_id)}>
                      {item.featured == 1 && (
                        <span className="badge-featured">★ Featured</span>
                      )}

                      {item.highlight_text && (
                        <span className="badge-highlight">
                          <BsTagsFill className="me-1" /> {formatProductText(item.highlight_text)}
                        </span>
                      )}

                      {hasDiscount && (
                        <span className="badge-discount">{discountPercentage}% OFF</span>
                      )}

                      <img
                        src={item.img1 || "placeholder.png"}
                        alt={formatProductText(item.product_name)}
                        className="unique-card-img"
                        loading="lazy"
                      />
                    </div>

                    {/* Card Body */}
                    <div className="unique-card-body">
                      <div className="unique-card-category">{item.category}</div>
                      <h5 className="unique-card-title" onClick={() => handleViewDetails(item.product_id)}>
                        {formatProductText(item.product_name)}
                      </h5>

                      {/* Rating */}
                      <div className="unique-card-rating">
                        <BsStarFill />
                        <BsStarFill />
                        <BsStarFill />
                        <BsStarFill />
                        <BsStarFill />
                        <span className="text-muted ms-1 small fw-semibold">(4.9)</span>
                      </div>

                      {/* Price Box */}
                      <div className="unique-price-box">
                        <span className="unique-offer-price">₹{activePrice}</span>
                        {hasDiscount && <span className="unique-regular-price">₹{regularPrice}</span>}
                        {item.weight > 0 && <span className="unique-weight-text">{item.weight} g</span>}
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex gap-2 mb-2">
                        <button
                          className="btn-unique-cart d-inline-flex align-items-center justify-content-center gap-1"
                          onClick={(e) => handleAddToCart(e, item)}
                        >
                          <BsCartPlus className="fs-6" /> Add to Cart
                        </button>
                        <button
                          className="btn-unique-buy d-inline-flex align-items-center justify-content-center gap-1"
                          onClick={(e) => handleBuyNow(e, item)}
                        >
                          <BsBagCheck className="fs-6" /> Buy Now
                        </button>
                      </div>

                      <button
                        className="btn-unique-details d-inline-flex align-items-center justify-content-center gap-1"
                        onClick={() => handleViewDetails(item.product_id)}
                      >
                        View Details <BsArrowRight />
                      </button>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          /* EMPTY STATE */
          <div className="unique-empty-state">
            <div className="display-4 text-muted mb-3">🌿</div>
            <h3 className="fw-bold text-dark mb-2">No Unique Products Available</h3>
            <p className="text-muted max-w-500 mx-auto mb-4">
              We are currently updating our handpicked unique catalog. Please check back later or explore our full product catalog!
            </p>
            <Button
              variant="success"
              className="rounded-pill px-4 py-2 fw-bold"
              onClick={() => navigate("/herbalProduct")}
            >
              Browse Catalog
            </Button>
          </div>
        )}
      </Container>
      <ToastContainer />
    </div>
  );
}
